import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  telstraPartnerService,
  type TelstraInvoiceItem,
} from '../../services/telstraService';

interface TelstraInvoicesPanelProps {
  customerId: string;
  customerName: string;
}

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

export function TelstraInvoicesPanel({ customerId, customerName }: TelstraInvoicesPanelProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [invoices, setInvoices] = useState<TelstraInvoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({
    invoiceNumber: '',
    period: currentMonth(),
    date: today(),
    dueDate: today(),
    amount: '',
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInvoices(await telstraPartnerService.listInvoices(customerId));
    } catch (err: any) {
      setInvoices([]);
      setError(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !form.invoiceNumber.trim() || !form.amount || !confirmed) return;

    setSaving(true);
    setError(null);
    try {
      await telstraPartnerService.uploadInvoice(customerId, file, {
        invoiceNumber: form.invoiceNumber.trim(),
        period: form.period,
        date: form.date,
        dueDate: form.dueDate,
        amount: Number(form.amount),
      });
      setForm((current) => ({ ...current, invoiceNumber: '', amount: '' }));
      setConfirmed(false);
      if (fileRef.current) fileRef.current.value = '';
      await loadInvoices();
    } catch (err: any) {
      setError(err?.message || 'Failed to upload invoice');
    } finally {
      setSaving(false);
    }
  };

  const download = async (invoice: TelstraInvoiceItem) => {
    setDownloadingId(invoice.id);
    setError(null);
    try {
      const blob = await telstraPartnerService.downloadInvoice(customerId, invoice.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Telstra_Invoice_${invoice.invoiceNumber || invoice.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        Invoice documents are enabled as a customer-specific legacy exception for <strong>{customerName}</strong>.
        Scheduled Blitz requests remain Excel-only.
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <FileText className="h-5 w-5 text-orange-600" />
          <h3 className="font-semibold text-slate-900">Upload invoice PDF</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">Invoice number</span>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.invoiceNumber}
              onChange={(event) => setForm({ ...form, invoiceNumber: event.target.value })} />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">Statement period</span>
            <input type="month" className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.period}
              onChange={(event) => setForm({ ...form, period: event.target.value })} />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">Amount</span>
            <input type="number" min="0" step="0.01" className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })} />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">Invoice date</span>
            <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.date}
              onChange={(event) => setForm({ ...form, date: event.target.value })} />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">Due date</span>
            <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.dueDate}
              onChange={(event) => setForm({ ...form, dueDate: event.target.value })} />
          </label>
          <label className="text-sm text-slate-700">
            <span className="block font-medium mb-1.5">PDF file</span>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </label>
        </div>
        <label className="flex items-start gap-2 mt-4 text-sm text-slate-700">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-0.5" />
          I confirm this invoice belongs to {customerName}.
        </label>
        <div className="flex justify-end mt-4">
          <Button disabled={saving || !confirmed || !form.invoiceNumber.trim() || !form.amount} onClick={() => void upload()}>
            {saving ? 'Uploading…' : 'Upload invoice'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Available invoices</h3>
          <Button variant="outline" size="sm" onClick={() => void loadInvoices()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {error && <div className="m-5 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No invoices uploaded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-6 py-3 text-left">Invoice</th>
                  <th className="px-6 py-3 text-left">Period</th>
                  <th className="px-6 py-3 text-left">Amount</th>
                  <th className="px-6 py-3 text-left">Due date</th>
                  <th className="px-6 py-3 text-right">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-3 font-medium text-slate-900">{invoice.invoiceNumber}</td>
                    <td className="px-6 py-3 text-slate-600">{invoice.period}</td>
                    <td className="px-6 py-3 text-slate-900">${Number(invoice.amount).toFixed(2)}</td>
                    <td className="px-6 py-3 text-slate-600">{invoice.dueDate}</td>
                    <td className="px-6 py-3 text-right">
                      <Button variant="outline" size="sm" disabled={downloadingId !== null} onClick={() => void download(invoice)}>
                        <Download className="h-4 w-4 mr-2" />
                        {downloadingId === invoice.id ? 'Downloading…' : 'Download PDF'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
